import { useLocation, useParams, useSearchParams } from "react-router-dom";
import moment from "moment";
import { useCallback, useMemo, useState } from "react";

import {
  ICreateWorkOrder,
  ICreateWorkOrderForm,
  ICurrentAppointment,
  IWorkOrder,
  IWorkOrderDetails,
} from "core/types/entities/IWorkOrder";
import { IVehicle } from "core/types/entities";
import { IContact } from "core/types/entities/IContact";
import { IUser } from "core/types/base/IUser";
import { useSelectOptions, useEntityManagement, useHandleModals } from "hooks/core";
import { IEquipment } from "core/types/entities/IEquipment";
import { API, userByTechnicianOnlyCanDoFilter } from "constants/RestApi";
import { formatISOtoStandart, timeLocalToUtcFormat } from "core/utils";
import { toUTCString, fromUTCString } from "core/utils/timezone-utils";
import { curremntAppointmentConst, watchingAppointmentKey } from "constants/WorkOrderData";
import { checklengthArray } from "core/utils/checkLength";
import { convertTechServerStatusToUI } from "core/utils/DataConverting";
import mapCalendarAppts, { IAbsenceAppts, WorkOrderAssignment } from "core/helpers/mapActiveAppts";
import useISODate from "hooks/core/useISODate";

const actions = ["contact", "equipment"] as const;

const useSubWOAddEdit = () => {
  const [searchParams] = useSearchParams();
  const estimateId = searchParams.get("estimateId");
  const { id } = useParams();
  const location = useLocation();
  const { getISODateTimezone } = useISODate();
  const { modals, toggleModal } = useHandleModals<IWorkOrderDetails, typeof actions[number]>({
    modalsKeys: actions,
  });
  const [currentAppointment, setCurrentAppointment] =
    useState<ICurrentAppointment>(curremntAppointmentConst);

  const isWorkOrderPage = (): boolean => {
    return location.pathname.includes("work-orders");
  };

  const { getByIdAction, createEditAction } = useEntityManagement<IWorkOrder | ICreateWorkOrder>({
    baseUrl: isWorkOrderPage() ? API.SUB_WORK_ORDERS : API.WORK_ORDERS,
    expand: "Vehicles,Techs,Contacts,Equipment",
  });

  const { selectOptions: vehicles, getOptions: getVehicles } = useSelectOptions<IVehicle>({
    fetchOnLoad: false,
    orderby: "name",
    url: API.VEHICLES,
  });

  const { selectOptions: contacts, getOptions: getContactsOptions } = useSelectOptions<IContact>({
    fetchOnLoad: false,
    orderby: "firstName",
    url: API.COMPANIES_CONTACTS,
  });

  const { selectOptions: equipments, getOptions: getEquipmentOptions } =
    useSelectOptions<IEquipment>({
      fetchOnLoad: false,
      orderby: "name",
      url: API.COMPANIES_EQUIPMENT,
      expand: "equipmentModel($expand=make($select=name),type($select=name);$select=model)",
    });

  const { selectOptions: users, getOptions: getUsers } = useSelectOptions<IUser>({
    fetchOnLoad: false,
    url: API.USERS,
    orderby: "firstName",
    filterParam: userByTechnicianOnlyCanDoFilter,
  });

  const mappingSubWO = (form: ICreateWorkOrderForm): ICreateWorkOrder => {
    // convert form data to backend schema
    return {
      ...form,
      endTime: toUTCString(form?.endTime) ?? "",
      startTime: toUTCString(form?.startTime) ?? "",
      vehicleIds: form?.vehicleIds?.value ? [Number(form?.vehicleIds?.value)] : [],
      contactIds: form.contactIds?.length
        ? form.contactIds.map(contact => Number(contact?.value))
        : [],
      techIds: form.techIds.length ? form.techIds.map(tech => tech?.value) : [],
      equipmentIds: form.equipmentIds?.length
        ? form.equipmentIds.map(equipment => Number(equipment?.value))
        : [],
    };
  };

  const onSubmit = async (formData: ICreateWorkOrderForm, workOrderId?: number) => {
    // We can create subWO from Quote or WO screen
    const defitionFormScreen = isWorkOrderPage()
      ? {
          ...mappingSubWO({ ...formData, estimateId: estimateId ? Number(estimateId) : 0 }),
          workOrderId,
        }
      : { estimateId: Number(id), subWorkOrders: [{ ...mappingSubWO(formData), workOrderId }] };

    await createEditAction(defitionFormScreen as ICreateWorkOrder);
  };

  const { getItems: getApointments, entityItems: activeAppointment } =
    useEntityManagement<WorkOrderAssignment>({
      baseUrl: API.CALENDAR,
      expand:
        "Users, Roles, OSR.SubWorkOrderAssignmentCalendarRecord/SubWorkOrder($expand=workOrder($expand=company($select=name, id);$select=company, number, estimateId, companyId, status, signatureId, isDeleted, id, rowVersion;);$select=workOrder, workOrderId, number, status, startTime, endTime, calendarRecordId, isDeleted,id, rowVersion)",
    });

  const definitionAppointmentQuery = <T extends ICreateWorkOrderForm>(name: string, form: T) => {
    if (watchingAppointmentKey.includes(name) && form?.startTime && form?.endTime) {
      getApointments(
        `startTime le ${timeLocalToUtcFormat(form?.endTime)} and endTime ge ${timeLocalToUtcFormat(
          form?.startTime,
        )} and recordType eq 'WorkOrderAssignment'`,
        false,
      );
    }
  };
  // and OSR.SubWorkOrderAssignmentCalendarRecord/SubWorkOrder/isDeleted eq false and OSR.SubWorkOrderAssignmentCalendarRecord/SubWorkOrder/status ne 'Canceled' and OSR.SubWorkOrderAssignmentCalendarRecord/SubWorkOrder/status ne
  const onChangeDateHandler = (newDate: Date) => {
    const firstDay = moment(newDate).startOf("month").startOf("week").toDate();
    const lastDay = moment(newDate).endOf("month").endOf("week").toDate();
    getApointments(
      `startTime le ${timeLocalToUtcFormat(lastDay)} and endTime ge ${timeLocalToUtcFormat(
        firstDay,
      )} and recordType eq 'WorkOrderAssignment'`,
      false,
    );
  };

  const { getItems: getAbsenceItems, entityItems: absenceItems } =
    useEntityManagement<IAbsenceAppts>({
      baseUrl: API.CALENDAR,
      expand: "Users, Roles",
    });

  const getAbsence = useCallback(
    (newDate: Date | string, includeGenericEvent = false) => {
      const firstDay = moment(newDate).startOf("month").startOf("week").toDate();
      const lastDay = moment(newDate).endOf("month").endOf("week").toDate();
      getISODateTimezone([firstDay, lastDay]).then(([startTime, endTime]) => {
        const recordTypeFilter = includeGenericEvent
          ? "(recordType eq 'Absence' or recordType eq 'GenericEvent')"
          : "recordType eq 'Absence'";
        getAbsenceItems(
          `startTime le ${endTime} and endTime ge ${startTime} and ${recordTypeFilter}`,
        );
      });
    },
    [getAbsenceItems, getISODateTimezone],
  );

  const getTooltipUsersText = (event: IAbsenceAppts) => {
    const roles = event?.roles?.map(x => x.name) || [];
    const usersList = event?.users?.map(x => `${x.firstName} ${x.lastName}`) || [];
    const combinedArray = [...roles, ...usersList];
    const str = combinedArray.length > 0 ? combinedArray.join(",") : "No users";
    return str;
  };

  const mappingAppointmentEvent = (order: any) => {
    // mapping Appointments data to events data for big calendar
    const filteredAssignTech = checklengthArray(order?.assignedUsers ?? [])
      ? order?.assignedUsers.map((tech: any) => `${tech?.firstName} ${tech?.lastName}`)
      : [];
    const uniqueTechNames = new Set(filteredAssignTech);
    const filteredAssignTechNames = Array.from(uniqueTechNames).join("/");

    // Convert UTC times to local Date objects for display
    const startTimeInTargetTz = fromUTCString(order.startTime);
    const endTimeInTargetTz = fromUTCString(order.endTime);

    return {
      ...order,
      startTime: startTimeInTargetTz,
      endTime: endTimeInTargetTz,
      title: filteredAssignTechNames ?? `Status: ${convertTechServerStatusToUI(order.status)}`,
      tooltip: `Customer: ${
        order.workOrder?.company.name
      }\nTechnicians: ${filteredAssignTechNames}\nWO ID: #${
        order?.number
      }\nStatus: ${convertTechServerStatusToUI(order.status)}\nDate: ${formatISOtoStandart(
        startTimeInTargetTz?.toString() ?? "",
      )} - ${formatISOtoStandart(endTimeInTargetTz?.toString() ?? "")}`,
    };
  };

  const absenceAppts = useMemo(() => {
    return absenceItems.map(event => {
      // If event is created for All users, show "All" in tooltip, otherwise show users list or "No users"
      const usersText = event.isForAllUser === true ? "All" : getTooltipUsersText(event);
      const description = event.description || event.type || event.title || "Generic event";
      const absenceTitle =
        event.recordType === "Absence"
          ? `${description}${usersText ? ` - ${usersText}` : ""}`
          : event.title || event.type;
      const genericTitle = event.title || event.type;
      return {
        ...event,
        hexColor: "#92D4AC",
        tooltip: `Description: ${description}\nDate: ${formatISOtoStandart(
          event.startTime,
        )} - ${formatISOtoStandart(event.endTime)}\nUsers: ${usersText}`,
        title: event.recordType === "GenericEvent" ? genericTitle : absenceTitle,
        displayTitle: event.recordType === "GenericEvent" ? genericTitle : absenceTitle,
        startTime: event.startTime,
        endTime: event.endTime,
      };
    });
  }, [absenceItems]);

  const getWOAppts = () => activeAppointment.filter(elem => elem.recordType !== "Absence");

  return {
    getAbsence,
    absenceAppts,
    getAbsenceItems,
    onSubmit,
    vehicles,
    users,
    contacts,
    equipments,
    getByIdAction,
    getEquipmentOptions,
    getContactsOptions,
    mappingSubWO,
    getVehicles,
    getUsers,
    mappingAppointmentEvent,
    getApointments,
    activeAppointment: mapCalendarAppts(getWOAppts()),
    definitionAppointmentQuery,
    modals,
    toggleModal,
    onChangeDateHandler,
    isWorkOrderPage,
    currentAppointment,
    setCurrentAppointment,
  };
};

export default useSubWOAddEdit;
